import './HeroCard.css';
import type { ComponentChildren } from 'preact';
import { Card } from '../../components/Card';
import { isPreHeadline } from '../../model/headline';
import type { HeadlineState, LayoutMode, ResultsView } from '../../model/types';
import { useHeadline, useLayout, useView } from '../../state/hooks';
import { Headline } from './Headline';
import { HeroKicker } from './HeroKicker';

/** Variante e partes visíveis do herói, derivadas da vista e do modo de layout (§7.2 blocos 2, 4 e 6; §7.16). */
export interface HeroFlags {
  /**
   * Sem snapshot, `national.validVotes = 0` ou manchete em `connecting`/`countdown`/`opened_no_votes` (§7.2 bloco 4):
   * `.hero-card--pre`, corpo = `PreResultsGrid` (§7.11). Antes das 17h00 nunca há % nem votos (E01, AC-7.17-03).
   */
  pre: boolean;
  /** Com votos e menos de 1% das seções: `.hero-card--early` (números esmaecidos, §7.5). */
  early: boolean;
  /** `mathDecision` ou `tseResult` nacional: `.hero-card--decided`. */
  decided: boolean;
  /** Variante TV (`.hero-card--tv`): sem kicker, 1º turno, "Ainda em apuração" e KPIs; candidatos não clicáveis. */
  tv: boolean;
  /** `land` (`.hero-card--compact`): sem "Ainda em apuração" e sem KPIs; retratos ocultos. */
  compact: boolean;
  /** Retratos dos líderes visíveis (ocultos no `flow` e no `land`, §7.2 bloco 6). */
  portraits: boolean;
  /** Candidatos clicáveis (todos os modos, salvo a TV). */
  interactive: boolean;
  /** Primeiro snapshot com números dentro da janela `intro` (§7.2 bloco 5): contagem de 90% a 100%. */
  intro: boolean;
}

/** Contexto das partes do corpo (só montadas com votos: a vista existe). */
export interface HeroBodyContext extends HeroFlags {
  view: ResultsView;
}
/** Contexto da grade pré-apuração (antes do 1º snapshot a vista é null; §7.11 usa os estáticos). */
export interface HeroPreContext extends HeroFlags {
  view: ResultsView | null;
}

/** Conteúdo de uma parte: o elemento pronto ou uma função do contexto (para quem precisa da vista e das variantes). */
export type HeroSlot<C> = ComponentChildren | ((c: C) => ComponentChildren);

/**
 * Partes do herói (§7.2 bloco 2), pelos nomes canônicos: cada uma é preenchida pelo componente da seção dona e é
 * montada só quando a anatomia a pede neste estado e neste modo. O espaçamento de cada parte é do próprio componente.
 */
export interface HeroCardProps {
  /** `PreResultsGrid` (§7.11): corpo da variante `--pre`. */
  pre?: HeroSlot<HeroPreContext>;
  /** `Countdown` (§10.8): dentro da manchete, só no estado `countdown`. */
  countdown?: ComponentChildren;
  /** `HeroLeaders` (§7.5). */
  leaders?: HeroSlot<HeroBodyContext>;
  /** `GoalBar` (§7.6). */
  goal?: HeroSlot<HeroBodyContext>;
  /** `MarginRows` variante `rows` (§7.7). */
  margin?: HeroSlot<HeroBodyContext>;
  /** Chance no topo (`ProbabilityStrip`, C01): logo depois da diferença; quem preenche decide se ela aparece. */
  chance?: HeroSlot<HeroBodyContext>;
  /** `FirstRoundRef` (§7.8): some na TV. */
  firstRound?: HeroSlot<HeroBodyContext>;
  /** `StillCountingList` (§7.9): só depois da definição; some na TV e no `land`. */
  stillCounting?: HeroSlot<HeroBodyContext>;
  /** `HeroKpis` (§7.10): some na TV e no `land`; poda por altura no próprio componente (`u-no-mid u-no-tiny`). */
  kpis?: HeroSlot<HeroBodyContext>;
  /** Primeiro snapshot com números dentro da janela `intro` (§7.2 bloco 5): %, votos e KPIs contam de 90% a 100%. */
  intro?: boolean;
  /**
   * Primeira montagem do cartão dentro da janela `intro`, na coluna esquerda (A02): `kf-enter` com 450 ms de atraso.
   * Fixo desde a montagem: ligar o atraso depois (quando os números chegam) reinicia o `kf-enter` já em curso ou
   * concluído, e o cartão some e reaparece (CSS Animations: a nova `animation-delay` vale desde o início da animação).
   */
  introEnter?: boolean;
}

function fill<C>(slot: HeroSlot<C> | undefined, c: C): ComponentChildren {
  return typeof slot === 'function' ? (slot as (c: C) => ComponentChildren)(c) : (slot ?? null);
}

/**
 * Pura: variante e partes do herói (§7.2) para a vista, o modo de layout e o estado exibido da manchete (o computed
 * `headline`, que acompanha o relógio na contagem e na abertura; sem ele, `view.headline`).
 */
export function heroFlags(
  view: ResultsView | null,
  mode: LayoutMode,
  intro = false,
  headline: HeadlineState | null = view?.headline ?? null,
): HeroFlags {
  const n = view?.national ?? null;
  const pre = n === null || n.validVotes === 0 || (headline !== null && isPreHeadline(headline));
  const tv = mode === 'tv';
  const compact = mode === 'land';
  return {
    pre,
    early: !pre && n !== null && n.sectionsFraction < 0.01,
    decided: !pre && n !== null && (n.tseResult ?? n.mathDecision) !== null,
    tv,
    compact,
    portraits: mode !== 'flow' && mode !== 'land',
    interactive: !tv,
    intro,
  };
}

/**
 * Cartão herói nacional de presidente (§7.2): sempre o placar do Brasil, que não muda com a seleção de lugar (F43).
 * `<section aria-labelledby="hero-headline">` sem `aria-live` (viradas e definições vão pela `LiveRegion`, §15.3);
 * estado visível = f(`view.headline`, vista) e nunca do estado da conexão (§13.1.1 regra 2, AC-7.2-11).
 */
export function HeroCard(props: HeroCardProps) {
  const view = useView();
  const { mode } = useLayout();
  const f = heroFlags(view, mode, props.intro === true, useHeadline());
  const classes = ['hero-card'];
  if (f.pre) classes.push('hero-card--pre');
  if (f.early) classes.push('hero-card--early');
  if (f.decided) classes.push('hero-card--decided');
  if (f.compact) classes.push('hero-card--compact');
  if (f.tv) classes.push('hero-card--tv');
  if (props.introEnter === true) classes.push('hero-card--intro');
  let body: ComponentChildren;
  if (f.pre || view === null) {
    body = fill(props.pre, { ...f, view });
  } else {
    const c: HeroBodyContext = { ...f, view };
    body = (
      <>
        {fill(props.leaders, c)}
        {fill(props.goal, c)}
        {fill(props.margin, c)}
        {f.tv ? null : fill(props.chance, c)}
        {f.tv ? null : fill(props.firstRound, c)}
        {f.tv || f.compact || !f.decided ? null : fill(props.stillCounting, c)}
        {f.tv || f.compact ? null : fill(props.kpis, c)}
      </>
    );
  }
  return (
    <Card class={classes.join(' ')} data-testid="hero-card" labelledBy="hero-headline">
      {f.tv ? null : <HeroKicker intro={f.intro} pre={f.pre} />}
      <Headline countdown={props.countdown} />
      {body}
    </Card>
  );
}
